/**
 * Animation Manager for FlowRide Frontend
 * Manages real-time vehicle animations on the map using the centralized AnimationOrchestrator.
 * Handles smooth vehicle movement and map updates for live tracking.
 */

import { type VehicleCustomLayer } from "../components/MapLibre/VehicleCustomLayer";
import { animationOrchestrator } from "./AnimationOrchestrator";
import { type VehicleManager } from "./VehicleManager";

/** Unique callback ID for vehicle animation in the orchestrator */
const VEHICLE_ANIMATION_CALLBACK_ID = "vehicle-animation";

export class AnimationManager {

    /** Vehicle manager instance for accessing vehicle data */
    vehicleManager!: VehicleManager;


    /** Vehicle custom layer for map rendering */
    vehicleLayer!: VehicleCustomLayer;


    /** Whether animation is currently running */
    isAnimating!: boolean;

    /** FPS tracking for render */
    private renderFrameCount: number = 0;

    private lastRenderFpsUpdateMs: number = 0;

    private currentRenderFps: number = 0;

    /** Callback to report FPS stats */
    private onFpsUpdate: ( ( renderFps: number ) => void ) | null = null;

    /** Maximum render FPS configured */
    private maxRenderFps!: number;

    /**
     * Create a new AnimationManager instance
     * @param vehicleManager - Vehicle manager instance
     * @param vehicleLayer - Vehicle custom layer for rendering
     * @param maxRenderFps - Maximum render rate (fps)
     */
    constructor( vehicleManager: VehicleManager, vehicleLayer: VehicleCustomLayer, maxRenderFps: number ) {
        this.vehicleManager = vehicleManager;
        this.vehicleLayer = vehicleLayer;
        this.isAnimating = false;
        this.maxRenderFps = maxRenderFps;
        this.lastRenderFpsUpdateMs = performance.now();
    }

    /**
     * Set callback for FPS updates (for debug overlay)
     * @param callback - Callback receiving render FPS
     */
    setOnFpsUpdate( callback: ( ( renderFps: number ) => void ) | null ): void {
        this.onFpsUpdate = callback;
    }

    /**
     * Get current render FPS
     */
    getRenderFps(): number {
        return this.currentRenderFps;
    }

    /**
     * Get maximum render FPS configured
     */
    getMaxRenderFps(): number {
        return this.maxRenderFps;
    }

    /**
     * Start the animation loop by registering with the AnimationOrchestrator
     */
    start(): void {
        if ( this.isAnimating ) {
            return;
        }
        this.isAnimating = true;
        this.lastRenderFpsUpdateMs = performance.now();
        animationOrchestrator.register( VEHICLE_ANIMATION_CALLBACK_ID, this.animateVehicles, this.maxRenderFps );
    }

    /**
     * Stop the animation loop by unregistering from the AnimationOrchestrator
     */
    stop(): void {
        this.isAnimating = false;
        animationOrchestrator.unregister( VEHICLE_ANIMATION_CALLBACK_ID );
    }

    /**
     * Update the maximum render rate (fps)
     */
    setMaxRenderFps( maxRenderFps: number ): void {
        this.maxRenderFps = maxRenderFps;
        animationOrchestrator.setMaxFps( VEHICLE_ANIMATION_CALLBACK_ID, maxRenderFps );
    }

    /**
     * Animation callback for vehicle updates
     * Called by the AnimationOrchestrator at the configured FPS rate
     * @param _deltaTime - Time since last frame (ms) - unused but available for frame-rate independent animation
     * @param now - Current timestamp (ms)
     */
    private readonly animateVehicles = ( _deltaTime: number, now: number ): void => {
        // Update vehicle positions
        this.vehicleLayer.updateVehiclePositions();

        // Request map repaint via orchestrator (batched with other repaint requests)
        animationOrchestrator.requestRepaint();

        // Track render FPS
        this.renderFrameCount++;
        const elapsed = now - this.lastRenderFpsUpdateMs;
        if ( elapsed >= 1000 ) {
            this.currentRenderFps = Math.round( ( this.renderFrameCount / elapsed ) * 1000 );
            this.renderFrameCount = 0;
            this.lastRenderFpsUpdateMs = now;
            this.onFpsUpdate?.( this.currentRenderFps );
        }
    };

}
